import React from "react";
import { User } from "../../_models";
import AuthenticationService from "../../_services/AuthenticationService";
import "./home.css";
import { RouteComponentProps } from "react-router";
import { ReactComponent as UserProfile } from "../../assets/icons/user.svg";
import { TabActivities } from "../tab-activites";
import { TabDashboard } from "../tab-dashboard";
import Config from "../../_config/config";
import { ITeachConfigContext } from "../../_context/PresetContext";
import Sidebar from "../../components/Sidebar/Sidebar";
import TenantPrefsGear from "../../components/TenantPrefsGear/TenantPrefsGear";
import PortletService, { Portlet } from "../../_services/PortletService";
import WorkspaceContainer from "./workspace/WorkspaceContainer";

interface MyProps extends RouteComponentProps {}

type MyState = {
  activeView: 'landing' | 'activities' | 'sales' | 'dashboard';
  loading: boolean;
  sidebarOpen: boolean;
  portlets: Portlet[];
  portletsLoading: boolean;
  portletsError: boolean;
  profilePicFailed: boolean;
  logoFailed: boolean;
};

export class Home extends React.Component<MyProps, MyState> {
  static contextType = ITeachConfigContext;
  currentUser: User;

  state: MyState = {
    activeView: 'landing',
    loading: true,
    sidebarOpen: false,
    portlets: [],
    portletsLoading: true,
    portletsError: false,
    profilePicFailed: false,
    logoFailed: false
  };

  constructor(props: MyProps) {
    super(props);
    this.currentUser = AuthenticationService.currentUserValue;
    if (this.currentUser === undefined || this.currentUser === null) {
      this.props.history.replace("/login");
    }
  }

  async componentDidMount() {
    // Check for query parameters to show specific tab
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const activeTab = urlParams.get("activetab");

    if (activeTab === 'activities') {
      this.setState({ activeView: 'activities' });
    } else if (activeTab === 'sales') {
      this.setState({ activeView: 'sales' });
    } else if (activeTab === 'dashboard') {
      this.setState({ activeView: 'dashboard' });
    }

    // Load portlets from cache/database
    await this.loadPortlets();

    // Simulate loading (remove if you want instant render)
    setTimeout(() => {
      this.setState({ loading: false });
    }, 300);
  }

  loadPortlets = async () => {
    try {
      this.setState({ portletsLoading: true, portletsError: false });

      // Get portlets from service (already filtered by roles on backend;
      // on outage the service returns the user's last known ACL or throws)
      const allPortlets = await PortletService.getPortlets();
      const filteredPortlets = PortletService.filterPortletsByRole(allPortlets);

      this.setState({
        portlets: filteredPortlets,
        portletsLoading: false
      });
    } catch (error) {
      console.error('Error loading portlets:', error);
      // Server unreachable and no cached ACL — fail closed, show nothing
      this.setState({
        portlets: [],
        portletsLoading: false,
        portletsError: true
      });
    }
  };

  navigateTo = (path: string) => {
    if (path.includes('?activetab=')) {
      // Handle internal tab navigation
      const tab = path.split('=')[1];
      this.setState({ activeView: tab as any });
      this.props.history.push(path);
    } else {
      // External navigation
      this.props.history.push(path);
    }
  };

  goBackToLanding = () => {
    this.setState({ activeView: 'landing' });
    this.props.history.push('/home');
  };

  toggleSidebar = () => {
    this.setState({ sidebarOpen: !this.state.sidebarOpen });
  };

  closeSidebar = () => {
    this.setState({ sidebarOpen: false });
  };

  render() {
    // Try multiple possible username fields from the JWT token
    const userName = this.currentUser?.enquiryName ||
                     this.currentUser?.userName ||
                     (this.currentUser as any)?.username ||
                     (this.currentUser as any)?.emailId ||
                     "User";
    const userProfilePic = this.currentUser?.photoFile || null;

    // Show loading state
    if (this.state.loading) {
      return (
        <>
        <Sidebar isOpen={this.state.sidebarOpen} onClose={this.closeSidebar} />
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'var(--gradient-brand)'
        }}>
          <div style={{
            textAlign: 'center',
            color: 'white'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              border: '4px solid rgba(255, 255, 255, 0.3)',
              borderTop: '4px solid white',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px'
            }} />
            <p style={{
              fontSize: '16px',
              fontWeight: 500,
              margin: 0
            }}>Loading...</p>
          </div>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
        </>
      );
    }

    // Show modern page content if not on landing page
    if (this.state.activeView === 'activities') {
      return (
        <>
        <Sidebar isOpen={this.state.sidebarOpen} onClose={this.closeSidebar} />
        <div className="modern-home">
          <div className="home-nav">
            <div className="home-nav-content">
              <button
                className="hamburger-button-home"
                onClick={this.toggleSidebar}
                aria-label="Toggle menu"
              >
                ☰
              </button>
              <button
                onClick={this.goBackToLanding}
                style={{
                  background: 'var(--brand-primary)',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '16px',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                ← Back
              </button>
              <div className="brand">
                <span className="brand-icon">📚</span>
                <span className="brand-text">Activities</span>
              </div>
              <TenantPrefsGear size={18} />
              <div className="user-info" onClick={() => this.navigateTo("/profile")}>
                {userProfilePic && !this.state.profilePicFailed ? (
                  <img
                    src={userProfilePic}
                    alt={userName}
                    className="user-avatar"
                    style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }}
                    onError={() => this.setState({ profilePicFailed: true })}
                  />
                ) : (
                  <UserProfile className="user-avatar" />
                )}
                <span className="user-name">{userName}</span>
              </div>
            </div>
          </div>
          <div className="modern-content">
            <TabActivities />
          </div>
        </div>
        </>
      );
    }

    if (this.state.activeView === 'sales') {
      return (
        <>
        <Sidebar isOpen={this.state.sidebarOpen} onClose={this.closeSidebar} />
        <div className="modern-home">
          <div className="home-nav">
            <div className="home-nav-content">
              <button
                className="hamburger-button-home"
                onClick={this.toggleSidebar}
                aria-label="Toggle menu"
              >
                ☰
              </button>
              <button
                onClick={this.goBackToLanding}
                style={{
                  background: 'var(--brand-primary)',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '16px',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                ← Back
              </button>
              <div className="brand">
                <span className="brand-icon">💼</span>
                <span className="brand-text">Sales & Follow-ups</span>
              </div>
              <TenantPrefsGear size={18} />
              <div className="user-info" onClick={() => this.navigateTo("/profile")}>
                {userProfilePic && !this.state.profilePicFailed ? (
                  <img
                    src={userProfilePic}
                    alt={userName}
                    className="user-avatar"
                    style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }}
                    onError={() => this.setState({ profilePicFailed: true })}
                  />
                ) : (
                  <UserProfile className="user-avatar" />
                )}
                <span className="user-name">{userName}</span>
              </div>
            </div>
          </div>
          <div className="modern-content">
            <div style={{ padding: 24, color: "var(--text-muted)" }}>Welcome</div>
          </div>
        </div>
        </>
      );
    }

    if (this.state.activeView === 'dashboard') {
      return (
        <>
        <Sidebar isOpen={this.state.sidebarOpen} onClose={this.closeSidebar} />
        <div className="modern-home">
          <div className="home-nav">
            <div className="home-nav-content">
              <button
                className="hamburger-button-home"
                onClick={this.toggleSidebar}
                aria-label="Toggle menu"
              >
                ☰
              </button>
              <button
                onClick={this.goBackToLanding}
                style={{
                  background: 'var(--brand-primary)',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '16px',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                ← Back
              </button>
              <div className="brand">
                <span className="brand-icon">📈</span>
                <span className="brand-text">Dashboard</span>
              </div>
              <TenantPrefsGear size={18} />
              <div className="user-info" onClick={() => this.navigateTo("/profile")}>
                {userProfilePic && !this.state.profilePicFailed ? (
                  <img
                    src={userProfilePic}
                    alt={userName}
                    className="user-avatar"
                    style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }}
                    onError={() => this.setState({ profilePicFailed: true })}
                  />
                ) : (
                  <UserProfile className="user-avatar" />
                )}
                <span className="user-name">{userName}</span>
              </div>
            </div>
          </div>
          <div className="modern-content">
            <TabDashboard />
          </div>
        </div>
        </>
      );
    }

    return (
      <>
      <Sidebar isOpen={this.state.sidebarOpen} onClose={this.closeSidebar} />
      <div className="modern-home">
        {/* Top Navigation Bar */}
        <div className="home-nav">
          <div className="home-nav-content">
            <button
              className="hamburger-button-home"
              onClick={this.toggleSidebar}
              aria-label="Toggle menu"
            >
              ☰
            </button>
            <div className="brand">
              <img
                className="home-brand-logo"
                src={(this.context as any).config?.logoUrl || `https://webfront.edusquares.com/${Config.site}/files/logos/logo.png`}
                alt="Institute Logo"
                onError={(e) => {
                  // Fallback to tenant name if logo fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent && !parent.querySelector('.brand-text')) {
                    const fallback = document.createElement('span');
                    fallback.className = 'brand-text';
                    fallback.textContent = Config.tenantDisplayName;
                    parent.appendChild(fallback);
                  }
                }}
              />
            </div>
            <div className="nav-actions">
              <TenantPrefsGear size={18} />
              <div className="user-info" onClick={() => this.navigateTo("/profile")}>
                {userProfilePic && !this.state.profilePicFailed ? (
                  <img
                    src={userProfilePic}
                    alt={userName}
                    className="user-avatar"
                    style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }}
                    onError={() => this.setState({ profilePicFailed: true })}
                  />
                ) : (
                  <UserProfile className="user-avatar" />
                )}
                <span className="user-name">{userName}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Hero Section */}
        <div className="hero-section">
          <div className="hero-content">
            <h1 className="hero-title">Welcome back, {userName}! 👋</h1>
            <p className="hero-subtitle">
              {(this.context as any).config?.displayName || Config.tenantDisplayName}
            </p>
          </div>
        </div>

        {/* Workspace Tabs + Feature Cards Grid */}
        {this.state.portletsError ? (
          <div className="home-portlets-error">
            <div className="home-portlets-error-card">
              <span className="home-portlets-error-icon" aria-hidden="true">⚠️</span>
              <h2>Unable to reach the server</h2>
              <p>Your apps can't be loaded right now. Please check your connection or try again in a moment.</p>
              <button
                onClick={this.loadPortlets}
                style={{
                  background: 'var(--brand-primary)',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '15px',
                  padding: '10px 24px',
                  borderRadius: '8px',
                  fontWeight: 600
                }}
              >
                Retry
              </button>
            </div>
          </div>
        ) : (
          <WorkspaceContainer
            portlets={this.state.portlets}
            onNavigate={this.navigateTo}
            userId={(this.currentUser as any)?.userId || 0}
          />
        )}

      </div>
      </>
    );
  }
}
